import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

const MAX_SETS = 8;
const PCS_PER_SET = 8;

const SetRows = ({ value, onChange }) => {
  const edit = (i, k, v) => onChange(value.map((s, n) => (n === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_7rem_2rem] gap-2 text-xs text-zinc-400"><span>Set Name</span><span>How many sets</span></div>
      <div className="text-[11px] text-zinc-500">Each set is {PCS_PER_SET} PCs.</div>
      {value.map((s, i) => (
        <div key={i} className="grid grid-cols-[1fr_7rem_2rem] gap-2">
          <Input data-testid={`higher-set-name-input-${i}`} required maxLength={80} value={s.name} placeholder={`Set name ${i + 1}`}
            onChange={(e) => edit(i, "name", e.target.value)} className="bg-zinc-900" />
          <Input data-testid={`higher-set-count-input-${i}`} type="number" min={1} required value={s.count} aria-label={`How many sets for set ${i + 1}`}
            onChange={(e) => edit(i, "count", e.target.value)} className="bg-zinc-900" />
          {value.length > 1 ? (
            <button type="button" data-testid={`higher-set-name-remove-${i}`} onClick={() => onChange(value.filter((_, k) => k !== i))}
              className="rounded-lg grid place-items-center text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-300"><X className="h-4 w-4" /></button>
          ) : <span />}
        </div>
      ))}
      {value.length < MAX_SETS && (
        <button type="button" data-testid="higher-set-name-add" onClick={() => onChange([...value, { name: "", count: "" }])}
          className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-sky-400/60 hover:text-white">
          <Plus className="h-3.5 w-3.5" /> Add set option ({value.length}/{MAX_SETS})
        </button>
      )}
    </div>
  );
};

const Toggle = ({ testId, checked, onChange, label, children }) => (
  <div className="space-y-2 rounded-xl border border-zinc-800 p-4">
    <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer">
      <Checkbox data-testid={testId} checked={checked} onCheckedChange={(v) => onChange(!!v)} /> {label}
    </label>
    {checked && children}
  </div>
);

export default function HigherDropFormDialog({ d, onClose, onSaved }) {
  const isEdit = !!d.id;
  const [f, setF] = useState({
    name: d.name || "", sets: d.sets?.length ? d.sets : [{ name: "", count: "" }],
    missing_items: !!d.missing_items, missing_sets: d.missing_sets || "", has_ems: !!d.has_ems, ems: d.ems || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const total = f.sets.reduce((n, s) => n + (Number(s.count) || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, sets: f.sets.map((s) => ({ name: s.name, count: Number(s.count) })), missing_sets: f.missing_items ? Number(f.missing_sets) : 0, ems: f.has_ems ? Number(f.ems) : 0 };
      if (isEdit) await api.put(`/higher/drops/${d.id}`, body);
      else await api.post("/higher/drops", body);
      toast.success(isEdit ? "Higher Drop updated" : "Higher Drop added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save Higher Drop"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="higher-drop-form-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit Higher Drop" : "Add Higher Drop"}</DialogTitle>
          <DialogDescription>Each joiner's EMS is the EMS amount × the number of PCs they have in the drop.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Higher Round Name</Label>
            <Input data-testid="higher-drop-name-input" required maxLength={120} value={f.name} onChange={(e) => set("name")(e.target.value)} className="bg-zinc-900" />
          </div>
          <div className="space-y-1.5">
            <Label>Sets</Label>
            <SetRows value={f.sets} onChange={set("sets")} />
            <div data-testid="higher-drop-total-sets" className="text-xs text-zinc-500">Total: {total} set{total === 1 ? "" : "s"} · {total * PCS_PER_SET} PCs</div>
          </div>
          <Toggle testId="higher-drop-missing-checkbox" checked={f.missing_items} onChange={set("missing_items")} label="Missing Items?">
            <Label className="text-xs text-zinc-400">How Many Missing Sets?</Label>
            <Input data-testid="higher-drop-missing-sets-input" type="number" min={1} max={total || undefined} required value={f.missing_sets} onChange={(e) => set("missing_sets")(e.target.value)} className="bg-zinc-900 w-32" />
          </Toggle>
          <Toggle testId="higher-drop-ems-checkbox" checked={f.has_ems} onChange={set("has_ems")} label="Is there EMS?">
            <Label className="text-xs text-zinc-400">EMS amount per PC ($)</Label>
            <Input data-testid="higher-drop-ems-input" type="number" min={0.01} step="0.01" required value={f.ems} onChange={(e) => set("ems")(e.target.value)} className="bg-zinc-900 w-32" />
          </Toggle>
          <Button type="submit" disabled={busy} data-testid="higher-drop-save-button" className="w-full h-11 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? "Save changes" : "Add Higher Drop"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
